import api from './api';

/**
 * Wallet Service - Handles all wallet-related API calls
 */
class WalletService {
  /**
   * Get wallet balance for the current user
   */
  async getBalance() {
    try {
      const response = await api.get('/wallet/balance');
      return response.data;
    } catch (error) {
      // Don't re-throw 404 or 500 errors to avoid triggering redirects
      if (error.response?.status === 404 || error.response?.status >= 500) {
        console.warn('Wallet service unavailable:', error.response?.status);
        return { success: false, data: null, error: 'Service unavailable' };
      }
      throw error;
    }
  }

  /**
   * Get transaction history with optional filters
   */
  async getTransactions(options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.page) params.append('page', options.page);
      if (options.limit) params.append('limit', options.limit);
      if (options.status) params.append('status', options.status);
      if (options.type) params.append('type', options.type);
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);
      if (options.sortBy) params.append('sortBy', options.sortBy);
      if (options.sortOrder) params.append('sortOrder', options.sortOrder);

      const response = await api.get(`/wallet/transactions?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }

  /**
   * Get wallet statistics
   */
  async getStatistics(period = '30d') {
    try {
      const response = await api.get(`/wallet/statistics?period=${period}`);
      return response.data;
    } catch (error) {
      // Don't re-throw 404 or 500 errors to avoid triggering redirects
      if (error.response?.status === 404 || error.response?.status >= 500) {
        console.warn('Wallet statistics service unavailable:', error.response?.status);
        return { success: false, data: null, error: 'Service unavailable' };
      }
      throw error;
    }
  }

  /**
   * Get wallet limits and usage
   */
  async getLimits() {
    try {
      const response = await api.get('/wallet/limits');
      return response.data;
    } catch (error) {
      // Don't re-throw 404 or 500 errors to avoid triggering redirects
      if (error.response?.status === 404 || error.response?.status >= 500) {
        console.warn('Wallet limits service unavailable:', error.response?.status);
        return { success: false, data: null, error: 'Service unavailable' };
      }
      throw error;
    }
  }

  /**
   * Transfer funds to another user
   */
  async transferFunds(toUserId, amount, description, metadata = {}) {
    try {
      const response = await api.post('/wallet/transfer', {
        toUserId,
        amount,
        description,
        metadata
      });
      return response.data;
    } catch (error) {
      console.error('Error transferring funds:', error);
      throw error;
    }
  }

  /**
   * Get specific transaction details
   */
  async getTransactionById(transactionId) {
    try {
      const response = await api.get(`/wallet/transactions/${transactionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      throw error;
    }
  }

  /**
   * Generate financial summary report
   */
  async getSummaryReport(startDate, endDate, groupBy = 'day') {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (groupBy) params.append('groupBy', groupBy);

      const response = await api.get(`/wallet/reports/summary?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching summary report:', error);
      throw error;
    }
  }

  /**
   * Generate activity report
   */
  async getActivityReport(options = {}) {
    try {
      const params = new URLSearchParams();
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);
      if (options.type) params.append('type', options.type);
      if (options.status) params.append('status', options.status);

      const response = await api.get(`/wallet/reports/activity?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching activity report:', error);
      throw error;
    }
  }

  /**
   * Purchase credit (mock payment for demo)
   */
  async purchaseCredit(amount) {
    try {
      const response = await api.post('/wallet/purchase-credit', {
        amount: parseFloat(amount)
      });
      return response.data;
    } catch (error) {
      console.error('Error purchasing credit:', error);
      throw error;
    }
  }
}

export default new WalletService();